import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { CarouselComponent } from "common/carousel/carousel.component";
import { UtilService } from "common/util.service";
import { TabName, WorkspaceService } from "common/workspace/workspace.service";
import { MainService } from "core/main.service";
import { PreferencesService } from "core/preferences.service";
import { combineLatest, EMPTY, from, fromEvent, Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { FolderRepository } from "common/workspace/folder.repository";
import { AsyncPipe, NgClass } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TrimDirective } from "common/trim.directive";
import { CarouselItemComponent } from "common/carousel/carousel-item.component";
import { WorkspaceListComponent } from "./workspace-list.component";
import { ClickStopPropagationDirective } from "common/click-stop-propagation.directive";
import { WorkspaceCarouselComponent } from "./workspace-carousel.component";

const DEBOUNCE_TIME = 250;
type Workspace = stages.workspace.Workspace;
type Folder = stages.workspace.Folder;

interface Preference {
	tabName: TabName;
}

export interface WorkspaceSelected {
	name: string;
	id: string;
	pathSegments: string[];
	path: string;
}

@Component({
	selector: "stages-workspace-selection",
	templateUrl: "./workspace-selection.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NgClass,
		TranslateModule,
		ReactiveFormsModule,
		TrimDirective,
		FormsModule,
		CarouselComponent,
		CarouselItemComponent,
		WorkspaceListComponent,
		ClickStopPropagationDirective,
		WorkspaceCarouselComponent,
		AsyncPipe,
	],
})
export class WorkspaceSelectionComponent implements OnInit, OnDestroy {
	private readonly mainService = inject(MainService);
	private readonly utilService = inject(UtilService);
	private readonly route = inject(ActivatedRoute);
	private readonly preferencesService = inject(PreferencesService);
	private chRef = inject(ChangeDetectorRef);
	private workspaceService = inject(WorkspaceService);
	private readonly folderRepository = inject(FolderRepository);

	folder$!: Observable<Folder | undefined>;
	workspaces$!: Observable<Workspace[]>;
	favorites$!: Observable<Set<string>>;
	currentWorkspaceId!: string;
	currentProcessVersion!: string;

	tabs!: TabName[];
	activeTab!: TabName;

	inputSearchTerm?: string;
	searchTerm?: string;
	searchEngineAvailable = true;

	readonly activeTabPreferenceKey = input<string>();

	readonly bookmarkable = input<boolean>();

	readonly classes = input<string[]>();

	readonly filterBy = input<string>();

	readonly filterParam1 = input<string>();

	readonly filterParam2 = input<string>();

	readonly selected = input<string>();

	readonly useBrowsePV = input<boolean>();

	readonly showRelated = input<boolean>();

	readonly showArchived = input<boolean>(true);

	readonly showFavorites = input<boolean>(true);

	readonly showSearch = input<boolean>(true);

	readonly showNavigate = input<boolean>(true);

	readonly selectWorkspace = output<WorkspaceSelected>();

	readonly input = viewChild.required("searchInput", { read: ElementRef });

	readonly carousel = viewChild.required<CarouselComponent>("carousel");

	private destroy$ = new Subject<boolean>();

	ngOnInit(): void {
		this.workspaceService.clearStore();
		this.folder$ = this.folderRepository.activeFolder$;

		const activeTabPreferenceKey = this.activeTabPreferenceKey();
		combineLatest([
			this.route.data,
			this.route.root.firstChild!.paramMap,
			this.route.paramMap,
			activeTabPreferenceKey
				? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
					from(this.preferencesService.getPreference<any>(activeTabPreferenceKey, undefined))
				: of(undefined),
		])
			.pipe(
				distinctUntilChanged(
					([data, rootParamMap, paramMap, preference], [data2, rootParamMap2, paramMap2, preference2]) =>
						rootParamMap.get("processVersion") === rootParamMap2.get("processVersion") &&
						paramMap.get("browseProcessVersion") === paramMap2.get("browseProcessVersion") &&
						data.related === data2.related &&
						data.onlyModules === data2.onlyModules,
				),
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(([data, rootParamMap, paramMap, preference]) => {
				this.currentWorkspaceId = rootParamMap.get("workspaceId")!;
				this.currentProcessVersion = this.useBrowsePV()
					? paramMap.get("browseProcessVersion")!
					: rootParamMap.get("processVersion")!;

				this.tabs = [];
				if (this.showRelated() && data.related && data.related.length > 0) {
					if (data.onlyModules) {
						this.tabs.push("modules");
					} else {
						this.tabs.push("related");
					}
				}

				if (this.showFavorites()) {
					this.tabs.push("favorites");
				}

				if (this.showNavigate()) {
					this.tabs.push("navigate");
				}

				if (this.showSearch()) {
					this.tabs.push("search");
				}

				if (this.showArchived()) {
					this.tabs.push("archived");
				}

				this.chRef.detectChanges();

				const storedTab = preference
					? // eslint-disable-next-line @typescript-eslint/prefer-find -- TODO: Remove this comment and fix warnings!
						this.tabs.filter((tab) => tab === (preference as Preference).tabName)[0]
					: undefined;
				this.openTab(storedTab ?? this.tabs[0], rootParamMap, paramMap, false);
			});

		fromEvent(this.input().nativeElement, "keyup")
			.pipe(debounceTime(DEBOUNCE_TIME))
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.searchTerm = this.inputSearchTerm;
				if (this.activeTab === "search") {
					this.search(this.filterBy(), this.filterParam1(), this.filterParam2());
				}
			});
	}

	openTab(tab: TabName, rootParamMap?: ParamMap, paramMap?: ParamMap, isAnimate: boolean = true): void {
		if (tab === "navigate") {
			this.mainService.getCurrentWorkspace().then((workspaceView) => {
				this.getWorkspaceFolderFor(workspaceView.currentWorkspace.parentId ?? workspaceView.currentWorkspace.id);
			});
		} else if (tab === "search") {
			this.favorites$ = from(
				this.workspaceService.getWorkspaces(
					"favorites",
					this.currentWorkspaceId,
					this.currentProcessVersion,
					this.filterBy(),
					this.filterParam1(),
					this.filterParam2(),
				),
			).pipe(
				map((favorites) => {
					const favoriteSet = new Set<string>();
					favorites.forEach((favorite) => {
						if (favorite.favorite) {
							favoriteSet.add(favorite.id!);
						}
					});
					return favoriteSet;
				}),
			);

			this.search(this.filterBy(), this.filterParam1(), this.filterParam2());
		} else {
			this.workspaces$ = from(
				this.workspaceService.getWorkspaces(
					tab,
					this.currentWorkspaceId,
					this.currentProcessVersion,
					this.filterBy(),
					this.filterParam1(),
					this.filterParam2(),
				),
			);
		}

		this.carousel().select(tab, isAnimate);
		this.activeTab = tab;

		if (!this.utilService.deviceIsIOS()) {
			this.input().nativeElement.focus();
		}

		const activeTabPreferenceKey = this.activeTabPreferenceKey();
		if (activeTabPreferenceKey) {
			this.preferencesService.setPreference<Preference>(activeTabPreferenceKey, { tabName: tab });
		}
	}

	onSelectWorkspace(workspaceChanged: WorkspaceSelected): void {
		this.clearSearchTermForCarouselSelection();
		this.selectWorkspace.emit(workspaceChanged);
	}

	onNavigate(workspaceId: string): void {
		this.clearSearchTermForCarouselSelection();
		this.getWorkspaceFolderFor(workspaceId);
		if (!this.utilService.deviceIsIOS()) {
			this.input().nativeElement.focus();
		}
	}

	private clearSearchTermForCarouselSelection(): void {
		// Clear the search term, when the workspace is selected in the "Navigate" tab or on navigation up/down.
		// In the "Navigate" tab the search term is (in most cases) only be used to find a child and is not useful for further navigation.
		const carousel = this.carousel();
		if (carousel.selection && carousel.selection.name() === "navigate") {
			this.inputSearchTerm = this.searchTerm = "";
		}
	}

	onBookmark(workspace: Workspace): void {
		this.workspaceService.toggleFavorite(workspace, this.currentWorkspaceId, this.currentProcessVersion);
	}

	private search(filterBy?: string, filterParam1?: string, filterParam2?: string): void {
		if (this.searchTerm) {
			this.workspaces$ = combineLatest([
				from(
					this.workspaceService.searchWorkspaces(
						this.searchTerm,
						this.currentWorkspaceId,
						this.currentProcessVersion,
						filterBy,
						filterParam1,
						filterParam2,
					),
				),
				this.favorites$,
			]).pipe(
				map(([workspaces, favorites]) => {
					workspaces.forEach((workspace) => (workspace.favorite = favorites.has(workspace.id!)));
					return workspaces;
				}),
				catchError(() => {
					this.searchEngineAvailable = false;
					return EMPTY;
				}),
			);
		} else {
			this.workspaces$ = of([]);
			this.searchEngineAvailable = true;
		}
	}

	private getWorkspaceFolderFor(workspaceId: string): void {
		if (this.folderRepository.hasItem(workspaceId)) {
			this.workspaceService.setActiveWorkspace(workspaceId);
		} else {
			this.workspaceService
				.loadWorkspaceFolder(
					workspaceId,
					this.filterBy() ?? null,
					this.filterParam1() ?? null,
					this.filterParam2() ?? null,
					this.currentWorkspaceId,
					this.currentProcessVersion,
				)
				.then(() => this.workspaceService.setActiveWorkspace(workspaceId));
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
		this.workspaceService.clearStore();
	}
}
